import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Banner, Button, CkThemeProvider, Field, Icon, Panel, ProgressBar, Select, Spinner, Stepper, Tag, TextInput, ToneIcon, useCkInside,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import { emailDomainsApi } from '../../services/emailDomainsApi';
import {
  providerGuide, PROVIDER_OPTIONS, MAIL_PROVIDER_NAMES, relativeName, zoneFile,
} from './dnsProviders';
import './emailDomainWizard.css';

/* ── wording ── */

/** one entry per wizard step: stepper label, what happens now, why it matters, how long it takes */
const STEPS = [
  {
    label: 'הדומיין',
    title: 'איזה דומיין מחברים?',
    lead: 'מזינים את הדומיין של הארגון — החלק שאחרי ה-@ בכתובת המייל, או כתובת האתר. נבדוק שהוא קיים, נזהה אצל מי הוא מנוהל ואיפה מתנהל הדואר שלכם.',
    why: 'בשלב הזה שום דבר עוד לא משתנה. זו רק בדיקה.',
    time: 'כדקה',
  },
  {
    label: 'כתובת השולח',
    title: 'מאיזו כתובת המיילים ייצאו?',
    lead: 'בוחרים את הכתובת ואת השם שהנמענים יראו בתיבת הדואר שלהם. למטה מופיעה תצוגה מקדימה בדיוק כפי שזה ייראה.',
    why: 'אפשר לשנות את הכתובת ואת השם גם אחרי החיבור, בלי לחזור על התהליך.',
    time: 'כדקה',
  },
  {
    label: 'רשומות DNS',
    title: 'מוסיפים את הרשומות אצל ספק הדומיין',
    lead: 'זה החלק העיקרי. נכנסים לאתר שבו מנהלים את הדומיין ומוסיפים כמה רשומות קצרות — מעתיקים מכאן ומדביקים שם. ההוראות מותאמות לספק שלכם.',
    why: 'הרשומות לא משנות את האתר ולא את תיבות הדואר. הן רק מאשרות לעולם שהמערכת רשאית לשלוח מיילים בשם הארגון.',
    time: '5 עד 10 דקות',
  },
  {
    label: 'אימות',
    title: 'בודקים שהרשומות נקלטו',
    lead: 'אנחנו בודקים אוטומטית כל חצי דקה אם הרשומות כבר נראות באינטרנט. כל רשומה שנמצאת מסומנת ב-✓, ואם משהו הוזן לא נכון — נגיד בדיוק מה לתקן.',
    why: 'שינויים ב-DNS מתעדכנים לרוב תוך כמה דקות, ולפעמים עד כמה שעות. אפשר לסגור את החלון ולחזור — נמשיך מאותה נקודה.',
    time: 'בדרך כלל כמה דקות',
  },
  {
    label: 'הפעלה',
    title: 'הכול מאומת — שולחים ניסיון ומפעילים',
    lead: 'שולחים קודם מייל ניסיון לעצמכם כדי לראות איך הוא מגיע. כשהכול נראה טוב, מפעילים — ומאותו רגע המיילים של המערכת יוצאים מהכתובת של הארגון.',
    why: 'מייל הניסיון נשלח רק לכתובת שתזינו, לא לאף נמען אחר.',
    time: 'כדקה',
  },
];

const PURPOSE = {
  ownership: { title: 'אישור בעלות', why: 'מוכיחה שהדומיין שלכם, ושרק המערכת הזו רשאית לשלוח בשמו.' },
  dkim: { title: 'חתימת מיילים (DKIM)', why: 'חותמת כל מייל, כדי שתיבות הדואר יזהו שהוא באמת מכם ולא יסננו אותו לספאם.' },
  dmarc: { title: 'מדיניות DMARC', why: 'מומלץ. מדיניות ניטור בלבד — משפרת את האמינות ולא חוסמת שום מייל.' },
};
const HINTS = {
  doubled: 'נראה שהשם הוזן מלא, והספק הוסיף את הדומיין פעם נוספת. ערכו את הרשומה והשאירו בשדה השם רק את השם המקוצר. כרגע נוצר:',
  proxied: 'הרשומה קיימת, אבל עוברת דרך ה-Proxy של Cloudflare (ענן כתום). ערכו אותה ושנו ל-DNS only (ענן אפור).',
  wrong: 'קיימת רשומה בשם הזה, אבל עם ערך אחר. ודאו שהערך הודבק במלואו, בלי רווחים ובלי תווים מיותרים.',
  missing: 'עוד לא נמצאה. אם כבר הוספתם אותה — זה רק עניין של זמן עד שהשינוי מתעדכן.',
};
const hintText = (hint, domain) => (hint === 'doubled'
  ? <>{HINTS.doubled} <span className="edw-ltr">…{domain}.{domain}</span></>
  : HINTS[hint] || HINTS.missing);
const recordFound = (domain, record) => (record.purpose === 'ownership' ? domain.checks?.ownership
  : record.purpose === 'dkim' ? domain.checks?.dkim?.find((r) => r.name === record.name)?.found : null);
/** the records the customer should add now (DMARC only when the domain has none) */
const recordsToAdd = (domain) => domain.records.filter((r) => r.purpose !== 'dmarc' || !domain.checks?.dmarc?.length);
const isEmail = (v) => /^[^\s<>@,;]+@[^\s<>@,;]+\.[^\s<>@,;]+$/.test(v);

function download(filename, text) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** plain-text instructions to hand to whoever manages the domain */
function instructionsText(domain, zone) {
  const lines = recordsToAdd(domain).map((r, i) => [
    `${i + 1}. ${PURPOSE[r.purpose].title}${r.required ? '' : ' (מומלץ)'}`,
    `   סוג: ${r.type}`,
    `   שם (Host): ${relativeName(r.name, zone)}    (שם מלא: ${r.name})`,
    `   ערך: ${r.value}`,
  ].join('\n'));
  return [
    `חיבור הדומיין ${domain.domain} לשליחת מיילים ממערכת הבחינות`,
    '',
    `יש להוסיף ב-DNS של ${zone} את הרשומות הבאות. TTL: אוטומטי / ברירת מחדל.`,
    '',
    ...lines,
    '',
    'חשוב:',
    '- לא למחוק ולא לשנות רשומות קיימות: MX, SPF, Google Workspace / Microsoft 365 נשארים כמו שהם.',
    '- ב-Cloudflare: רשומות ה-CNAME במצב DNS only (ענן אפור).',
    '- אם כבר קיימת רשומת _dmarc, לא מוסיפים עוד אחת.',
    '- להשאיר את הרשומות גם אחרי האימות. מחיקתן תעצור את השליחה מהדומיין.',
  ].join('\n');
}

/* ── small pieces ── */

/** copy button that confirms in place, so the eye stays on the record being copied */
function CopyButton({ value, label = 'העתקה', onError, size = 'sm', variant = 'default', icon = 'copy' }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1800);
    } catch { onError?.('לא ניתן להעתיק אוטומטית. סמנו את הטקסט והעתיקו ידנית'); }
  };
  return (
    <Button size={size} variant={variant} icon={copied ? 'check' : icon} onClick={copy} className={copied ? 'edw-copied' : ''} aria-live="polite">
      {copied ? 'הועתק' : label}
    </Button>
  );
}

function CopyValue({ value, alt, onError }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div className="edw-copy">
        <code>{value}</code>
        <CopyButton value={value} onError={onError} />
      </div>
      {alt ? <span className="edw-copy__alt">{alt}</span> : null}
    </div>
  );
}

function StepIntro({ index }) {
  const s = STEPS[index];
  return (
    <div className="edw-intro">
      <ToneIcon icon={['search', 'user', 'list', 'refresh', 'check'][index]} tone="primary" size={36} />
      <div className="edw-intro__body">
        <span className="edw-intro__kicker">
          שלב {index + 1} מתוך {STEPS.length}
          <Tag tone="neutral" icon="clock">{s.time}</Tag>
        </span>
        <h4>{s.title}</h4>
        <p>{s.lead}</p>
        <span className="edw-intro__why">{s.why}</span>
      </div>
    </div>
  );
}

/** stepper whose finished / visited steps are buttons, so any earlier step can be reopened */
function StepNav({ active, reached, canOpen, onOpen }) {
  return (
    <ol className="ck-steps edw-steps" aria-label="שלבים">
      {STEPS.map((s, i) => {
        const state = i === active ? 'active' : i < active || i < reached ? 'done' : 'todo';
        const open = i !== active && i <= reached && canOpen(i);
        const inner = (
          <>
            <span className="ck-steps__dot">{state === 'done' ? <Icon name="check" size={13} /> : i + 1}</span>
            <span className="ck-steps__label">{s.label}</span>
          </>
        );
        return (
          <li key={s.label} className={`ck-steps__item is-${state} ${open ? 'is-link' : ''}`.trim()} aria-current={state === 'active' ? 'step' : undefined}>
            {open ? <button type="button" onClick={() => onOpen(i)} title={`מעבר לשלב: ${s.label}`}>{inner}</button> : inner}
          </li>
        );
      })}
    </ol>
  );
}

function Actions({ back, children }) {
  return (
    <div className="edw-actions">
      {back || <span />}
      <div className="edw-actions__end">{children}</div>
    </div>
  );
}

function InboxPreview({ name, email }) {
  return (
    <div className="edw-inbox" aria-label="תצוגה מקדימה בתיבת הדואר">
      <div className="edw-inbox__bar">כך זה ייראה בתיבת הדואר של הנמען</div>
      <div className="edw-inbox__row">
        <span className="edw-inbox__avatar" aria-hidden="true">{(name || '?').trim().charAt(0) || '?'}</span>
        <span className="edw-inbox__who">
          <b>{name || 'שם הארגון'}</b>
          <small className="edw-ltr">{email || 'news@example.org'}</small>
        </span>
        <span className="edw-inbox__subject">ההרשמה התקבלה</span>
      </div>
    </div>
  );
}

/** one address typed by the admin — users here have no profile email */
function TestMail({ busy, onSend, label = 'שליחת מייל ניסיון' }) {
  const [to, setTo] = useState('');
  const valid = isEmail(to.trim());
  return (
    <div className="edw-row">
      <Field label="לאיזו כתובת לשלוח?" hint="כתובת אחת בלבד, למשל המייל שלכם">
        <TextInput ltr placeholder="you@example.org" value={to} maxLength={254} onChange={(e) => setTo(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && valid && !busy) onSend(to.trim()); }} />
      </Field>
      <Button icon="mail" disabled={busy || !valid} onClick={() => onSend(to.trim())} style={{ marginBottom: 22 }}>{label}</Button>
    </div>
  );
}

function RecordCard({ index, record, domain, zone, guide, isCloudflare, onError }) {
  const found = recordFound(domain, record);
  const shortName = relativeName(record.name, zone);
  const showShort = guide.relative && shortName !== record.name;
  return (
    <section className={`edw-rec ${found ? 'is-found' : ''}`.trim()} aria-label={`רשומה: ${PURPOSE[record.purpose].title}`}>
      <header className="edw-rec__head">
        <span className="edw-rec__num">{found ? <Icon name="check" size={13} /> : (record.required ? index : '+')}</span>
        <span className="edw-rec__title">{PURPOSE[record.purpose].title}</span>
        {!record.required ? <Tag tone="info">רשות, מומלץ</Tag> : null}
        {found ? <Tag tone="success" icon="check">נמצאה</Tag> : null}
      </header>
      <div className="edw-rec__body">
        <span className="edw-rec__why">{PURPOSE[record.purpose].why}</span>
        <div className="edw-rec__grid">
          <span className="edw-rec__label">סוג<small>Type</small></span>
          <span><span className="edw-type">{record.type}</span></span>
          <span className="edw-rec__label">שם<small>Name / Host</small></span>
          <CopyValue value={showShort ? shortName : record.name} alt={showShort ? `אם הספק מבקש שם מלא: ${record.name}` : null} onError={onError} />
          <span className="edw-rec__label">{record.type === 'CNAME' ? 'יעד' : 'ערך'}<small>{record.type === 'CNAME' ? 'Target / Value' : 'Value / Content'}</small></span>
          <CopyValue value={record.value} onError={onError} />
        </div>
        {isCloudflare && record.type === 'CNAME' ? <Banner tone="warning">ב-Cloudflare: Proxy status חייב להיות DNS only (ענן אפור)</Banner> : null}
      </div>
    </section>
  );
}

function CheckRow({ ok, fix, title, detail }) {
  return (
    <div className={`edw-check ${ok ? 'is-ok' : ''} ${!ok && fix ? 'has-fix' : ''}`.trim()}>
      <span className="edw-check__ic" aria-hidden="true"><Icon name={ok ? 'check' : fix ? 'alert' : 'clock'} size={14} /></span>
      <span className="edw-check__text">
        <b>{title}</b>
        <small>{ok ? 'נמצאה ותקינה' : detail}</small>
      </span>
    </div>
  );
}

/* ── intro: what this is and how it goes ── */

const PATH = [
  { title: 'הדומיין', text: 'מזינים את הדומיין, ואנחנו מזהים איפה הוא מנוהל', time: 'דקה' },
  { title: 'כתובת השולח', text: 'בוחרים כתובת ושם, למשל news@ בשם הארגון', time: 'דקה' },
  { title: 'רשומות DNS', text: 'מעתיקים 4 רשומות לאתר של ספק הדומיין, עם הוראות מדויקות', time: '5 עד 10 דקות' },
  { title: 'אימות', text: 'הבדיקה רצה לבד ומסמנת כל רשומה שנמצאה', time: 'כמה דקות' },
  { title: 'הפעלה', text: 'מייל ניסיון לעצמכם, ואז מפעילים', time: 'דקה' },
];

function Intro({ onStart }) {
  return (
    <Panel>
      <div className="edw">
        <div className="edw-hero">
          <ToneIcon icon="mail" tone="primary" size={48} />
          <div>
            <h3>שלחו מיילים מהכתובת של הארגון</h3>
            <p>אחרי החיבור, המיילים שהמערכת שולחת — דיוורים, אישורי רישום והודעות אוטומטיות — יוצאים בשם הארגון ומהכתובת שלו, למשל <b className="edw-ltr">news@your-org.org.il</b>.</p>
          </div>
        </div>

        <div className="edw-gains">
          <div className="edw-gain"><ToneIcon icon="inbox" tone="success" size={30} /><span><b>מגיעים לתיבה הנכנסת</b><small>מיילים חתומים מהדומיין שלכם נחשבים אמינים, ופחות נופלים לספאם.</small></span></div>
          <div className="edw-gain"><ToneIcon icon="users" tone="info" size={30} /><span><b>הנמענים מזהים אתכם</b><small>השם והכתובת של הארגון — לא של מערכת חיצונית.</small></span></div>
          <div className="edw-gain"><ToneIcon icon="reply" tone="violet" size={30} /><span><b>תשובות מגיעות אליכם</b><small>כשנמען עונה למייל, התשובה מגיעה לתיבה של הארגון.</small></span></div>
        </div>

        <div>
          <b style={{ display: 'block', marginBottom: 8 }}>איך זה עובד — 5 שלבים, כ-10 דקות בסך הכול</b>
          <ol className="edw-path">
            {PATH.map((p) => (
              <li key={p.title}><b>{p.title}</b><small>{p.text}</small><span className="edw-time">{p.time}</span></li>
            ))}
          </ol>
        </div>

        <div className="edw-cols">
          <div className="edw-box edw-box--info">
            <b><Icon name="key" size={15} />מה צריך</b>
            <ul>
              <li>גישה לאתר שבו מנהלים את הדומיין (רשם הדומיין או חברת האחסון של האתר).</li>
              <li>אין גישה? אפשר להעביר את ההוראות לאיש המחשבים בלחיצה אחת.</li>
            </ul>
          </div>
          <div className="edw-box edw-box--ok">
            <b><Icon name="check" size={15} />מה לא משתנה</b>
            <ul>
              <li>האתר של הארגון ממשיך לעבוד כרגיל.</li>
              <li>תיבות הדואר הקיימות (Gmail, Outlook וכו') לא מושפעות.</li>
              <li>אפשר לעצור באמצע ולהמשיך אחר כך מאותה נקודה.</li>
            </ul>
          </div>
        </div>

        <div className="edw-faq">
          <details>
            <summary><Icon name="help" size={15} />מה זה DNS, ולמה צריך לגעת בו?</summary>
            <div>DNS הוא "ספר הכתובות" של הדומיין — שם מוגדר איפה האתר ואיפה הדואר. כדי שתיבות הדואר בעולם יאמינו שמייל מהמערכת באמת נשלח בשמכם, מוסיפים שם כמה רשומות שמאשרות זאת. מוסיפים בלבד — לא מוחקים ולא משנים כלום.</div>
          </details>
          <details>
            <summary><Icon name="help" size={15} />אני לא יודע איפה הדומיין שלי מנוהל</summary>
            <div>לא צריך לדעת מראש. בשלב הראשון אנחנו מזהים את זה אוטומטית ומציגים הוראות מותאמות לספק (Cloudflare, GoDaddy, LiveDNS, Wix ועוד).</div>
          </details>
          <details>
            <summary><Icon name="help" size={15} />מישהו אחר מנהל לנו את הדומיין</summary>
            <div>עוברים את שני השלבים הראשונים, ובשלב הרשומות לוחצים "העתקת ההוראות" או "הורדה כקובץ" ושולחים לו. כשהוא מסיים, חוזרים לכאן — הבדיקה תמשיך מאותה נקודה.</div>
          </details>
          <details>
            <summary><Icon name="help" size={15} />אפשר לבטל אחר כך?</summary>
            <div>כן. בכל רגע אפשר לכבות את השליחה מהדומיין, והמיילים יחזרו לצאת מכתובת ברירת המחדל.</div>
          </details>
        </div>

        <Actions>
          <Button variant="primary" icon="link" onClick={onStart}>בואו נתחיל</Button>
        </Actions>
      </div>
    </Panel>
  );
}

/* ── the wizard ── */

const POLL_MS = 30000;
const POLL_MAX = 40; // ~20 minutes of automatic checks while the wizard is open

function ConnectWizard({ api, existing, onClose, onChanged }) {
  const [step, setStep] = useState(existing ? (existing.ready ? 4 : 2) : 0);
  const [reached, setReached] = useState(existing ? (existing.ready ? 4 : 2) : 0);
  const [domain, setDomain] = useState(existing || null);
  const [domainInput, setDomainInput] = useState('');
  const [info, setInfo] = useState(null);
  const [local, setLocal] = useState(existing?.fromEmail ? existing.fromEmail.split('@')[0] : 'news');
  const [fromName, setFromName] = useState(existing?.fromName || '');
  const [providerKey, setProviderKey] = useState(existing?.checks?.dnsProvider || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [polls, setPolls] = useState(0);
  const [nextAt, setNextAt] = useState(null);
  const [, tick] = useState(0);
  const [done, setDone] = useState(false);
  const top = useRef(null);
  useEffect(() => { setReached((n) => Math.max(n, step)); }, [step]);

  const go =(n) => { setStep(n); setError(''); setNotice(null); top.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }); };
  const run = async (fn, okMessage) => {
    setBusy(true); setError(''); setNotice(null);
    try { const r = await fn(); if (okMessage) setNotice({ tone: 'success', text: okMessage }); return r; } catch (e) { setError(e.message); return null; } finally { setBusy(false); }
  };

  const zone = domain?.checks?.dnsZone || info?.zone || domain?.domain || '';
  const guide = providerGuide(providerKey);
  const records = domain ? recordsToAdd(domain) : [];
  const required = records.filter((r) => r.required).length;
  const fromEmail = `${local.trim()}@${domain?.domain || info?.domain || ''}`;

  /* step 0 → inspect */
  const inspect = async () => {
    const r = await run(() => api.inspect(domainInput));
    if (!r) return;
    setInfo(r);
    if (r.dnsProvider) setProviderKey(r.dnsProvider);
  };
  const senderDomain = domain?.domain || info?.domain || '';
  const mailProvider = info?.mailProvider || null;
  /* step 1 again, after the domain exists → save the sender, records stay the same */
  const saveSender = async () => {
    if (fromEmail === domain.fromEmail && fromName.trim() === domain.fromName) { go(domain.ready ? 4 : 2); return; }
    const r = await run(() => api.update(domain.id, { fromEmail, fromName }));
    if (!r) return;
    setDomain((d) => ({ ...d, ...r }));
    onChanged();
    go(domain.ready ? 4 : 2);
  };
  /* step 1 → create, records arrive */
  const create = async () => {
    const r = await run(() => api.create({ domain: info.domain, fromEmail, fromName }));
    if (!r) return;
    setDomain(r);
    if (r.checks?.dnsProvider) setProviderKey(r.checks.dnsProvider);
    onChanged();
    go(r.ready ? 4 : 2);
  };
  /* step 3 → check (manual + polling) */
  const check = useCallback(async (quiet) => {
    if (!domain) return;
    try {
      const r = await api.action(domain.id, 'check');
      setDomain(r);
      if (r.ready) { setStep(4); onChanged(); }
      else if (!quiet) setNotice({ tone: 'info', text: 'עוד לא הכול נמצא. זה תקין — הבדיקה ממשיכה לבד כל חצי דקה.' });
    } catch (e) { if (!quiet) setError(e.message); }
  }, [api, domain, onChanged]);
  const timer = useRef(null);
  useEffect(() => {
    if (step !== 3 || polls >= POLL_MAX || domain?.ready) { setNextAt(null); return undefined; }
    setNextAt(Date.now() + POLL_MS);
    timer.current = setTimeout(() => { setPolls((n) => n + 1); check(true); }, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [step, polls, check, domain?.ready]);
  useEffect(() => {
    if (!nextAt) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [nextAt]);

  const checks = domain?.checks || {};
  const dkim = checks.dkim || [];
  const dkimFound = dkim.filter((r) => r.found).length;
  const foundCount = (checks.ownership ? 1 : 0) + dkimFound + (checks.ses ? 1 : 0);
  const totalCount = 1 + Math.max(dkim.length, 3) + 1;
  const secondsLeft = nextAt ? Math.max(0, Math.ceil((nextAt - Date.now()) / 1000)) : null;

  const closeLabel = done ? 'סגירה' : 'שמירה וסגירה';

  return (
    <Panel>
      <div className="edw" ref={top}>
        <div className="edw-head">
          <div>
            <h3>{existing ? <>השלמת החיבור · <span className="edw-ltr">{existing.domain}</span></> : 'חיבור הדומיין של הארגון'}</h3>
            {!done ? <small>ההתקדמות נשמרת. אפשר לחזור לשלב קודם בלחיצה על שמו, ולסגור בכל שלב ולהמשיך מאותה נקודה.</small> : null}
          </div>
          <Button variant="ghost" icon="x" onClick={onClose}>{closeLabel}</Button>
        </div>

        {done ? <Stepper steps={STEPS.map((s) => s.label)} active={STEPS.length} /> : (
          <StepNav active={step} reached={reached} canOpen={(i) => i !== 4 || !!domain?.ready} onOpen={(i) => go(i)} />
        )}

        {!done ? <StepIntro index={step} /> : null}
        {error ? <Banner tone="danger" onClose={() => setError('')}>{error}</Banner> : null}
        {notice ? <Banner tone={notice.tone} onClose={() => setNotice(null)}>{notice.text}</Banner> : null}

        {/* 0 · domain — already created: shown, not editable (its records are tied to it) */}
        {step === 0 && domain && (
          <div className="edw-body">
            <div className="edw-box edw-box--ok">
              <b><Icon name="check" size={15} />הדומיין שנבחר: <span className="edw-ltr">{domain.domain}</span></b>
              <p>הרשומות שהוכנו שייכות לדומיין הזה, ולכן אי אפשר לשנות אותו כאן. כדי לחבר דומיין אחר: סגרו את האשף ולחצו "חיבור דומיין נוסף".</p>
            </div>
            <Actions>
              <Button variant="primary" onClick={() => go(1)}>המשך לכתובת השולח</Button>
            </Actions>
          </div>
        )}

        {/* 0 · domain */}
        {step === 0 && !domain && (
          <div className="edw-body">
            <div className="edw-row">
              <Field label="הדומיין של הארגון" hint={<>לדוגמה <span className="edw-ltr">chesed.org.il</span>, בלי <span className="edw-ltr">https://</span>, בלי <span className="edw-ltr">www</span> ובלי <span className="edw-ltr">@</span></>}>
                <TextInput
                  ltr placeholder="example.org.il" value={domainInput} autoFocus maxLength={253} disabled={busy}
                  onChange={(e) => { setDomainInput(e.target.value); setInfo(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && domainInput.trim()) inspect(); }}
                />
              </Field>
              <Button icon={busy ? undefined : 'search'} onClick={inspect} disabled={busy || !domainInput.trim()} style={{ marginBottom: 22 }}>
                {busy ? <><Spinner size={14} /> בודק…</> : 'בדיקת הדומיין'}
              </Button>
            </div>
            {info && (info.exists ? (
              <div className="edw-cols">
                <div className="edw-box edw-box--ok">
                  <b><Icon name="check" size={15} />מצאנו את <span className="edw-ltr">{info.domain}</span></b>
                  <p>{info.dnsProvider
                    ? <>ה-DNS שלו מנוהל ב-<b>{providerGuide(info.dnsProvider).name}</b>. בשלב הרשומות נראה לכם בדיוק איפה ללחוץ שם.</>
                    : 'לא זיהינו אוטומטית איפה מנוהל ה-DNS. זה בסדר — בשלב הרשומות תבחרו את הספק מרשימה, או תקבלו הוראות כלליות.'}</p>
                </div>
                {info.mailProvider ? (
                  <div className="edw-box edw-box--info">
                    <b><Icon name="mail" size={15} />הדואר שלכם ב-{MAIL_PROVIDER_NAMES[info.mailProvider]}</b>
                    <p>החיבור לא נוגע בו. המיילים הנכנסים ימשיכו להגיע כרגיל.</p>
                  </div>
                ) : null}
              </div>
            ) : (
              <Banner tone="danger">
                לא מצאנו את <b className="edw-ltr">{info.domain}</b>. בדקו את האיות. אם הדומיין נרכש ממש עכשיו, ייתכן שהוא עוד לא פעיל — נסו שוב בעוד שעה.
              </Banner>
            ))}
            <Actions>
              <Button variant="primary" onClick={() => go(1)} disabled={!info?.exists}>המשך לכתובת השולח</Button>
            </Actions>
          </div>
        )}

        {/* 1 · sender */}
        {step === 1 && (info || domain) && (
          <div className="edw-body">
            <div className="edw-row">
              <Field label="כתובת השולח" hint="מילה קצרה באנגלית, למשל news, office, info">
                <span className="edw-suffix">
                  <TextInput ltr value={local} maxLength={64} onChange={(e) => setLocal(e.target.value.replace(/[@\s]/g, ''))} />
                  <span>@{senderDomain}</span>
                </span>
              </Field>
              <Field label="שם השולח" hint="השם שהנמען רואה, בדרך כלל שם הארגון">
                <TextInput placeholder="שם הארגון" value={fromName} maxLength={120} onChange={(e) => setFromName(e.target.value)} />
              </Field>
            </div>
            <InboxPreview name={fromName} email={fromEmail} />
            <div className="edw-box edw-box--info">
              <b><Icon name="reply" size={15} />כדאי לדעת</b>
              <p>
                {mailProvider
                  ? <>כשנמען עונה למייל, התשובה מגיעה לכתובת הזו. ודאו שקיימת עבורה תיבה או הפניה ב-{MAIL_PROVIDER_NAMES[mailProvider]}, אחרת התשובות לא יגיעו.</>
                  : 'כשנמען עונה למייל, התשובה מגיעה לכתובת הזו. ודאו שיש לה תיבת דואר אצלכם, אחרת התשובות לא יגיעו.'}
              </p>
            </div>
            <Actions back={<Button variant="ghost" onClick={() => go(0)} disabled={busy}>חזרה</Button>}>
              <Button variant="primary" onClick={domain ? saveSender : create} disabled={busy || !local.trim() || !fromName.trim()}>
                {busy ? <><Spinner size={14} /> {domain ? 'שומר…' : 'מכין את הרשומות…'}</> : domain ? (domain.ready ? 'שמירה והמשך להפעלה' : 'שמירה והמשך לרשומות') : 'המשך לרשומות ה-DNS'}
              </Button>
            </Actions>
          </div>
        )}

        {/* 2 · DNS records */}
        {step === 2 && domain && (
          <div className="edw-body">
            <div className="edw-row">
              <Field label="איפה מנוהל ה-DNS של הדומיין?" hint={checks.dnsProvider ? 'זוהה אוטומטית לפי הדומיין. אפשר לשנות אם זה לא נכון.' : 'לא זוהה אוטומטית. בחרו מהרשימה, או השאירו "אחר" להוראות כלליות.'}>
                <Select value={providerKey} onChange={setProviderKey} options={[...PROVIDER_OPTIONS.map((p) => ({ value: p.key, label: p.name })), { value: '', label: 'אחר / לא יודע' }]} />
              </Field>
            </div>

            <Panel title={`א. נכנסים ל-${guide.name}`} sub="עושים את זה בלשונית נפרדת, ומשאירים את החלון הזה פתוח" icon="external" tone="info"
              actions={guide.url ? <Button size="sm" icon="external" onClick={() => window.open(guide.url, '_blank', 'noopener,noreferrer')}>פתיחת {guide.name}</Button> : null}>
              <div className="edw edw-guide">
                <ol>{guide.steps.map((s) => <li key={s}>{s}</li>)}</ol>
                {guide.zoneImport ? <div className="edw-box edw-box--dashed"><b><Icon name="sparkle" size={15} />קיצור דרך</b><p>{guide.zoneImport}</p></div> : null}
              </div>
            </Panel>

            <div>
              <b style={{ display: 'block', marginBottom: 4 }}>ב. מוסיפים {required} רשומות, כל אחת בנפרד</b>
              <span className="edw-foot">בכל רשומה: בוחרים את הסוג, ומעתיקים את השם ואת הערך בלחיצה על "העתקה". את ה-TTL משאירים כמו שהוא.</span>
            </div>
            {records.map((r, i) => (
              <RecordCard key={r.name} index={i + 1} record={r} domain={domain} zone={zone} guide={guide} isCloudflare={providerKey === 'cloudflare'} onError={setError} />
            ))}
            {checks.dmarc?.length ? <Banner tone="success">לדומיין כבר יש מדיניות DMARC. לא מוסיפים עוד אחת ולא משנים אותה.</Banner> : null}

            <div className="edw-box edw-box--warn">
              <b><Icon name="alert" size={15} />חשוב</b>
              <ul>
                <li>לא מוחקים ולא משנים רשומות קיימות — MX, SPF ורשומות של Google / Microsoft נשארים כמו שהם.</li>
                <li>משאירים את הרשומות החדשות גם אחרי האימות. מחיקתן תעצור את השליחה.</li>
              </ul>
            </div>

            <div className="edw-box edw-box--dashed">
              <b><Icon name="users" size={15} />מישהו אחר מנהל לכם את הדומיין?</b>
              <p className="edw-foot" style={{ marginBottom: 10 }}>שלחו לו את ההוראות — הן כוללות את כל הרשומות ואת מה שאסור לשנות. כשהוא מסיים, חוזרים לכאן ולוחצים "הוספתי".</p>
              <div className="edw-dom__btns">
                <CopyButton value={instructionsText(domain, zone)} label="העתקת ההוראות" icon="copy" size="md" onError={setError} />
                <Button icon="download" onClick={() => download(`dns-${domain.domain}.txt`, instructionsText(domain, zone))}>הורדה כקובץ</Button>
                <Button variant="ghost" icon="file" onClick={() => download(`dns-${domain.domain}.zone`, zoneFile(records))}>קובץ לייבוא (Cloudflare / Route 53)</Button>
              </div>
            </div>

            <Actions back={<Button variant="ghost" onClick={() => go(1)}>חזרה לכתובת השולח</Button>}>
              <Button variant="primary" icon="check" onClick={() => { go(3); setPolls(0); check(false); }}>הוספתי את הרשומות — לבדיקה</Button>
            </Actions>
          </div>
        )}

        {/* 3 · verify */}
        {step === 3 && domain && (
          <div className="edw-body">
            <div className="edw-meter">
              <div className="edw-meter__top">
                <span>{foundCount} מתוך {totalCount} בדיקות עברו</span>
                {secondsLeft != null
                  ? <span className="edw-auto"><Spinner size={14} />בדיקה הבאה בעוד {secondsLeft} שניות</span>
                  : <span className="edw-auto">הבדיקה האוטומטית נעצרה. לחצו "בדיקה עכשיו".</span>}
              </div>
              <ProgressBar value={(foundCount / totalCount) * 100} tone={foundCount === totalCount ? 'success' : 'primary'} label="התקדמות האימות" />
            </div>
            <div className="edw-checks">
              <CheckRow ok={!!checks.ownership} fix={!!checks.ownershipHint} title="רשומת אישור הבעלות (TXT)" detail={hintText(checks.ownershipHint, domain.domain)} />
              {dkim.map((r, i) => (
                <CheckRow key={r.name} ok={r.found} fix={!!r.hint} title={`רשומת חתימה ${i + 1} מתוך ${dkim.length} (CNAME)`} detail={hintText(r.hint, domain.domain)} />
              ))}
              <CheckRow ok={!!checks.ses} title="אישור שירות השליחה" detail={dkimFound === dkim.length && dkim.length ? 'רשומות החתימה נמצאו. האישור הסופי מתעדכן לרוב תוך כמה דקות — אין צורך לעשות כלום.' : 'מתעדכן לבד אחרי שכל רשומות החתימה יימצאו.'} />
            </div>
            {domain.checkedAt ? <span className="edw-foot">בדיקה אחרונה: <time>{new Date(domain.checkedAt).toLocaleString('he-IL')}</time></span> : null}

            <div className="edw-faq">
              <details>
                <summary><Icon name="help" size={15} />עבר זמן והרשומות עדיין לא נמצאו?</summary>
                <div>
                  <ul>
                    <li>ודאו שהרשומות נוספו אצל הספק שמנהל <b>בפועל</b> את ה-DNS{zone ? <> של <span className="edw-ltr">{zone}</span></> : null}{checks.dnsProvider ? <> — לפי הבדיקה שלנו זה <b>{providerGuide(checks.dnsProvider).name}</b></> : null}.</li>
                    <li>בשדה השם הזינו רק את השם המקוצר, אם הספק מוסיף את הדומיין לבד.</li>
                    <li>ב-Cloudflare: הענן ליד רשומות ה-CNAME צריך להיות אפור (DNS only).</li>
                    <li>עבר יותר מיום? פנו למנהל המערכת ונבדוק יחד.</li>
                  </ul>
                </div>
              </details>
            </div>

            <Actions back={<Button variant="ghost" onClick={() => go(2)}>חזרה לרשומות</Button>}>
              {domain.ready ? <Button variant="primary" onClick={() => go(4)}>הכול תקין — המשך להפעלה</Button> : (
                <Button variant="primary" icon={busy ? undefined : 'refresh'} onClick={() => run(() => check(false))} disabled={busy}>
                  {busy ? <><Spinner size={14} /> בודק…</> : 'בדיקה עכשיו'}
                </Button>
              )}
            </Actions>
          </div>
        )}

        {/* 4 · activate */}
        {step === 4 && domain && (done ? (
          <div className="edw-done">
            <ToneIcon icon="check" tone="success" size={56} />
            <h4>הדומיין מחובר ופעיל</h4>
            <p>מעכשיו המיילים של המערכת יוצאים מ-<b className="edw-ltr">{domain.fromEmail}</b>, בשם <b>{domain.fromName}</b>.</p>
            <div className="edw-box">
              <b><Icon name="info" size={15} />מה עכשיו</b>
              <ul>
                <li>הכתובת משמשת את כל המיילים שהמערכת שולחת.</li>
                <li>השאירו את רשומות ה-DNS במקומן — הן נבדקות לפני כל שליחה.</li>
                <li>אפשר לשנות את שם השולח או לכבות את השליחה מהדומיין בכל רגע, מהמסך הזה.</li>
              </ul>
            </div>
            <Button variant="primary" onClick={onClose}>סיום</Button>
          </div>
        ) : (
          <div className="edw-body">
            <Banner tone="success">הדומיין <b className="edw-ltr">{domain.domain}</b> אומת בהצלחה.</Banner>
            <InboxPreview name={domain.fromName} email={domain.fromEmail} />
            <div className="edw-cols">
              <div className="edw-box">
                <b><span className="edw-rec__num">1</span>מייל ניסיון (מומלץ)</b>
                <p className="edw-foot" style={{ marginBottom: 10 }}>נשלח רק לכתובת שתזינו. בדקו שהוא הגיע לתיבה הנכנסת ושהשם נראה נכון.</p>
                <TestMail busy={busy} label="שליחת ניסיון" onSend={(recipient) => run(() => api.action(domain.id, 'test', { recipient }), `מייל הניסיון נשלח אל ${recipient}`)} />
              </div>
              <div className="edw-box">
                <b><span className="edw-rec__num">2</span>הפעלה</b>
                <p className="edw-foot" style={{ marginBottom: 10 }}>מהרגע הזה כל המיילים של המערכת יוצאים מהכתובת של הארגון. אפשר לכבות בכל רגע.</p>
                <Button variant="primary" icon="check" disabled={busy} onClick={async () => { const r = await run(() => api.action(domain.id, 'activate')); if (r) { setDomain(r); setDone(true); onChanged(); } }}>הפעלת שליחה מהדומיין</Button>
              </div>
            </div>
            <Actions back={<Button variant="ghost" onClick={() => go(1)}>חזרה לכתובת השולח</Button>} />
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* ── connected domains ── */

function DomainCard({ domain, busy, onAction, onSave, onResume }) {
  const [editing, setEditing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [email, setEmail] = useState(domain.fromEmail);
  const [name, setName] = useState(domain.fromName);
  useEffect(() => { setEmail(domain.fromEmail); setName(domain.fromName); }, [domain.fromEmail, domain.fromName]);
  const status = domain.active && !domain.ready ? { tone: 'danger', label: 'השליחה חסומה' }
    : domain.active ? { tone: 'success', label: 'פעיל לשליחה' }
      : domain.ready ? { tone: 'info', label: 'מאומת, עוד לא הופעל' } : { tone: 'warning', label: 'ממתין להשלמת החיבור' };
  return (
    <Panel>
      <div className="edw edw-dom">
        <div className="edw-head">
          <div className="edw-hero">
            <ToneIcon icon="mail" tone={status.tone} size={36} />
            <div>
              <div className="edw-dom__name edw-ltr">{domain.domain}</div>
              <div className="edw-dom__sender">
                <Tag tone={status.tone}>{status.label}</Tag>
                <span>שולח: <b>{domain.fromName}</b> · <span className="edw-ltr">{domain.fromEmail}</span></span>
              </div>
            </div>
          </div>
        </div>
        {domain.active && !domain.ready ? <Banner tone="danger">אימות הדומיין נכשל, כנראה כי רשומות DNS הוסרו. השליחה מהדומיין חסומה עד שהרשומות יחזרו. אפשר לכבות אותו כדי לחזור לכתובת ברירת המחדל.</Banner> : null}
        {!domain.ready && !domain.active ? <span className="edw-foot">החיבור עוד לא הושלם. לחצו "המשך חיבור" כדי לחזור בדיוק למקום שבו עצרתם.</span> : null}
        {editing ? (
          <div className="edw-row">
            <Field label="כתובת השולח"><TextInput ltr value={email} maxLength={254} onChange={(e) => setEmail(e.target.value)} /></Field>
            <Field label="שם השולח"><TextInput value={name} maxLength={120} onChange={(e) => setName(e.target.value)} /></Field>
            <Button variant="primary" disabled={busy || !email || !name} onClick={async () => { if (await onSave(domain.id, { fromEmail: email, fromName: name })) setEditing(false); }} style={{ marginBottom: 1 }}>שמירה</Button>
          </div>
        ) : null}
        {testing && domain.ready ? <TestMail busy={busy} onSend={async (recipient) => { if (await onAction(domain.id, 'test', { recipient })) setTesting(false); }} /> : null}
        <div className="edw-dom__btns">
          {!domain.ready ? <Button variant="primary" icon="link" onClick={() => onResume(domain)}>המשך חיבור</Button> : null}
          {domain.ready && !domain.active ? <Button variant="primary" icon="check" disabled={busy} onClick={() => onAction(domain.id, 'activate')}>הפעלת שליחה מהדומיין</Button> : null}
          {domain.ready ? <Button icon={testing ? 'x' : 'mail'} disabled={busy} onClick={() => setTesting((v) => !v)}>{testing ? 'ביטול ניסיון' : 'מייל ניסיון'}</Button> : null}
          <Button variant="ghost" icon="list" disabled={busy} onClick={() => onResume(domain, 2)}>רשומות DNS</Button>
          <Button variant="ghost" icon={editing ? 'x' : 'edit'} disabled={busy} onClick={() => setEditing((v) => !v)}>{editing ? 'ביטול' : 'עריכת השולח'}</Button>
          {domain.active ? <Button variant="ghost" disabled={busy} onClick={() => onAction(domain.id, 'disable')}>כיבוי וחזרה לכתובת ברירת המחדל</Button> : null}
        </div>
      </div>
    </Panel>
  );
}

function EmailDomainSettingsBody({ apiFactory = emailDomainsApi }) {
  const api = useMemo(() => apiFactory(), [apiFactory]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  /** null = list; { domain?, step? } = wizard (new or resuming a domain) */
  const [wizard, setWizard] = useState(null);
  const reload = useCallback(async (signal) => {
    const result = await api.list(signal);
    if (!signal?.aborted) setData(result);
    return result;
  }, [api]);
  useEffect(() => {
    const abort = new AbortController();
    setData(null); setError(''); setNotice(''); setLoading(true); setBusy(false); setWizard(null);
    reload(abort.signal).catch((e) => { if (!abort.signal.aborted) setError(e.message); }).finally(() => { if (!abort.signal.aborted) setLoading(false); });
    return () => abort.abort();
  }, [reload]);
  const perform = async (operation, message) => {
    setBusy(true); setError(''); setNotice('');
    try {
      await operation();
      await reload();
      setNotice(message);
      return true;
    } catch (e) { setError(e.message); return false; }
    finally { setBusy(false); }
  };
  const messages = { activate: 'הדומיין הופעל לשליחת מיילים מהמערכת', disable: 'הדומיין כובה. הודעות חדשות יישלחו מכתובת ברירת המחדל', test: 'מייל הניסיון נשלח' };
  const domains = data?.domains || [];
  const platformLimited = data?.platform && (!data.platform.production || !data.platform.sendingEnabled);

  return (
    <div className="edw" dir="rtl" aria-label="דומיין לשליחת מיילים">
      {error ? <Banner tone="danger" onClose={() => setError('')}>{error}</Banner> : null}
      {notice ? <Banner tone="success" onClose={() => setNotice('')}>{notice}</Banner> : null}
      {loading ? <span className="edw-auto" role="status"><Spinner size={18} />טוען…</span>
        : data?.enabled === false ? <Banner tone="info">שירות הדומיינים עדיין לא הופעל. יש לפנות למנהל המערכת להפעלתו.</Banner>
          : data && (wizard ? (
            <ConnectWizard
              key={wizard.domain?.id || 'new'}
              api={api}
              existing={wizard.domain ? { ...wizard.domain, ready: wizard.step === 2 ? false : wizard.domain.ready } : null}
              onChanged={() => reload().catch(() => {})}
              onClose={() => { setWizard(null); reload().catch(() => {}); }}
            />
          ) : (
            <>
              {platformLimited ? <Banner tone="warning">שירות השליחה עדיין בהרצה. אפשר כבר לחבר ולאמת את הדומיין. שליחה לכל הנמענים תיפתח כשמנהל המערכת ישלים את אישור השירות מול Amazon.</Banner> : null}
              {!domains.length ? <Intro onStart={() => setWizard({})} /> : (
                <>
                  {domains.map((d) => (
                    <DomainCard
                      key={d.id} domain={d} busy={busy}
                      onAction={(id, action, body) => perform(() => api.action(id, action, body), messages[action])}
                      onSave={(id, values) => perform(() => api.update(id, values), 'פרטי השולח נשמרו')}
                      onResume={(domain, step) => setWizard({ domain, step })}
                    />
                  ))}
                  {domains.length < 5 ? <div><Button icon="plus" onClick={() => setWizard({})}>חיבור דומיין נוסף</Button></div> : null}
                </>
              )}
              <span className="edw-foot">בכל מערכת פעיל דומיין אחד, וכל המיילים של המערכת יוצאים ממנו. ההגדרה לא משפיעה על מערכות אחרות.</span>
            </>
          ))}
      {!loading && !data ? <div><Button icon="refresh" onClick={() => perform(() => reload(), 'ההגדרות נטענו')} disabled={busy}>ניסיון נוסף</Button></div> : null}
    </div>
  );
}

/** crm-kit/next component; outside a next screen (old UI) it brings its own theme root */
export default function EmailDomainSettings(props) {
  const inside = useCkInside();
  const body = <EmailDomainSettingsBody {...props} />;
  return inside ? body : <CkThemeProvider>{body}</CkThemeProvider>;
}
